// reason: AsyncThunk<any, any, any> — generic Redux Toolkit helper plumbing.
//   The helper has no knowledge of the caller's specific Returned / ThunkArg /
//   ThunkApiConfig types; pinning anything here would fight RTK's type system.
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
    normalize,
    schema
} from 'normalizr';

import {
    ActionReducerMapBuilder,
    AsyncThunk
} from "@reduxjs/toolkit";

import {IReduxAction, RecordsSliceState} from "../types";

// FNP-336: helpers are generic over the slice's own state shape (T extends
// RecordsSliceState). Previously they were typed against RootState, which
// erased per-slice typing and caused a 78-error cascade once the store's
// `: Store` annotation was dropped.

export const fetchManyCase = <T extends RecordsSliceState>(builder: ActionReducerMapBuilder<T>, asyncThunk: AsyncThunk<any, any, any>, entityKey: string) => {

    builder.addCase(asyncThunk.pending, (state) => {
        state.loading = true;
    });

    builder.addCase(asyncThunk.fulfilled, (state, action: IReduxAction) => {
        state.loading = false;

        const entitySchema = new schema.Entity(entityKey);
        const arrayOfRecords = new schema.Array(entitySchema);

        const normalized = normalize(action.payload, arrayOfRecords);
        const {
            entities,
            result: ids
        } = normalized;

        state.byId = {...state.byId, ...entities[entityKey]};
        state.ids = [...new Set(state.ids.concat(ids))];

        state.totalCount = ids.length;
    });

    builder.addCase(asyncThunk.rejected, (state, action: IReduxAction) => {
        state.loading = false;
        state.error = action.error.message;
    });
};


export const fetchManyWithTokenPaginationCase = <T extends RecordsSliceState>(builder: ActionReducerMapBuilder<T>, asyncThunk: AsyncThunk<any, any, any>, entityKey: string) => {

    builder.addCase(asyncThunk.pending, (state) => {
        state.loading = true;
    });

    builder.addCase(asyncThunk.fulfilled, (state, action: IReduxAction) => {
        state.loading = false;

        const entitySchema = new schema.Entity(entityKey);
        const arrayOfRecords = new schema.Array(entitySchema);

        const normalized = normalize(action.payload.items, arrayOfRecords);
        const {
            entities,
            result: ids
        } = normalized;

        const nextToken = action.payload.nextToken || '';

        state.byId = {...state.byId, ...entities[entityKey]};
        state.ids = [...new Set(state.ids.concat(ids))];
        state.nextToken = nextToken;

        state.totalCount = ids.length;
    });

    builder.addCase(asyncThunk.rejected, (state, action: IReduxAction) => {
        state.loading = false;
        state.error = action.error.message;
    });
};


export const fetchOneCase = <T extends RecordsSliceState>(builder: ActionReducerMapBuilder<T>, asyncThunk: AsyncThunk<any, any, any>) => {
    builder.addCase(asyncThunk.pending, (state) => {
        state.loading = true;
    });

    builder.addCase(asyncThunk.fulfilled, (state, action: IReduxAction) => {
        state.byId[action.payload.id] = action.payload;
        state.loading = false;
    });

    builder.addCase(asyncThunk.rejected, (state, action: IReduxAction) => {
        state.loading = false;
        state.error = action.error.message;
    });
};

export const saveOneCase = <T extends RecordsSliceState>(builder: ActionReducerMapBuilder<T>, asyncThunk: AsyncThunk<any, any, any>) => {
    builder.addCase(asyncThunk.pending, (state) => {
        state.loading = true;
    });

    builder.addCase(asyncThunk.fulfilled, (state, action: IReduxAction) => {
        const {payload: record} = action;

        if (typeof record.uuid !== 'undefined') {
            state.byId[record.uuid] = record;
            state.ids.push(record.uuid);
        } else {
            state.byId[record.id] = record;
            state.ids.unshift(record.id);
        }

        state.ids = [...new Set(state.ids)];
        state.totalCount = state.ids.length;

        state.loading = false;
    });

    builder.addCase(asyncThunk.rejected, (state, action: IReduxAction) => {
        state.loading = false;
        state.error = action.error.message;
    });
};

export const updateOneCase = <T extends RecordsSliceState>(builder: ActionReducerMapBuilder<T>, asyncThunk: AsyncThunk<any, any, any>) => {
    builder.addCase(asyncThunk.pending, (state) => {
        state.loading = true;
    });

    builder.addCase(asyncThunk.fulfilled, (state, action: IReduxAction) => {
        const {payload: record} = action;

        if (typeof record.uuid !== 'undefined') {
            state.byId[record.uuid] = record;
        } else {
            state.byId[record.id] = record;
        }
        state.loading = false;
    });

    builder.addCase(asyncThunk.rejected, (state, action: IReduxAction) => {
        state.loading = false;
        state.error = action.error.message;
    });
};

export const deleteOneCase = <T extends RecordsSliceState>(builder: ActionReducerMapBuilder<T>, asyncThunk: AsyncThunk<any, any, any>) => {
    builder.addCase(asyncThunk.pending, (state) => {
        state.loading = true;
    });

    builder.addCase(asyncThunk.fulfilled, (state, action: IReduxAction) => {
        const {payload: recordId} = action;

        delete state.byId[recordId];
        state.ids = state.ids.filter(
            (item: string) => item.toString() !== recordId.toString()
        );
        state.totalCount = state.totalCount - 1;

        state.loading = false;
    });

    builder.addCase(asyncThunk.rejected, (state, action: IReduxAction) => {
        state.loading = false;
        state.error = action.error.message;
    });
};
